import React, {useEffect, useMemo, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import {Text} from '@forecast-it/design-system';
import ClientsListItem, {ClientRowData} from './ClientsListItem';
import ClientsPageHeader from './ClientsPageHeader';
import {MODAL_TYPE, showModal} from '../shared/components/modals/generic_modal_conductor';
import {CLIENT_PERMISSIONS_WITH_GUEST_USER} from '../admin-tab/clients-page/ClientPermissionGroups';
import {PERMISSION_TYPE} from '../../Permissions';
import {MODULE_TYPES, FEATURE_FLAGS} from '../../constants';
import {buildClientIdsWithContacts, computeClientHasContacts} from './clientContactsUtil';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {hasModule} from '../shared/util/ModuleUtil';
import CompanySetupUtil from '../shared/util/CompanySetupUtil';
import Util from '../shared/util/util';
import {createToast} from '../shared/components/toasts/toast';
import CreateClientModal from './CreateClientModal';
import CreateContactConfirmModal from './CreateContactConfirmModal';
import ClientContactModalContainer from '../admin-tab/clients-page/ClientContactModalContainer';
import {getSalesforceInstanceUrl} from '../admin-tab/clients-page/client-js/settings_clients_util';

// ---------------------------------------------------------------------------
// Styled layout — values match the design reference image
// ---------------------------------------------------------------------------

const PageWrapper = Styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 32px 16px 32px;
`;

const HeadingRow = Styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
`;

const Heading = Styled.h1`
  ${props => props.theme.sys.font.heading['3']};
  margin: 0;
  color: #232334;
`;

const ButtonGroup = Styled.div`
  display: flex;
  gap: 8px;
`;

// Secondary button — replace with design-system Button component when confirmed available
const SecondaryButton = Styled.button`
  height: 44px;
  padding: 0 12px;
  border: 1px solid #6b6d7b;
  border-radius: 8px;
  background: transparent;
  ${props => props.theme.sys.font.subheading['2']};
  color: #232334;
  cursor: pointer;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const Description = Styled.p`
  ${props => props.theme.sys.font.body['2']};
  color: #6b6d7b;
  margin: 0;
`;

const ClientsTable = Styled.table`
  width: 100%;
  border-collapse: collapse;
  /* auto layout: the right-hand columns size to their content (the project count + however many
     integration icons a row has), so nothing reserves a hardcoded slice of width. Notes (width: 100%)
     soaks up the remainder, which keeps the count/icons cluster snug against the right. */
  table-layout: auto;
`;

const TableHead = Styled.thead`
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 1;
  border-bottom: 1px solid #e5e5e5;
`;

const Th = Styled.th`
  padding: 8px 16px;
  text-align: left;
  ${props => props.theme.sys.font.subheading['2']};
  color: #6b6d7b;
`;

const ThRight = Styled(Th)`
  text-align: right;
`;

const ScrollContainer = Styled.div`
  overflow-y: auto;
  max-height: calc(100vh - 280px);
`;

const EmptyStateCell = Styled.td`
  padding: 24px 16px;
  text-align: center;
`;

// Fold case and strip diacritics so the term matches the way the list is ordered — the service
// sorts with `sensitivity: 'base'`, which treats "Cafe" and "Café" as the same word. Without this
// a search for "cafe" would miss "Café Rouge" that sorts as though the accent were absent.
const normalizeForSearch = (value: string) =>
	value
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.toLowerCase();

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ModalState =
	| {type: 'none'}
	| {type: 'createClient'}
	| {type: 'createContactConfirm'; clientId: string; clientName: string}
	| {type: 'createContact'; clientId: string; clientName: string};

type Profile = {
	id: string;
	name: string;
	permissions: string[];
};

// One top-of-list "Import from X" button. Which options appear is decided in the
// Relay container by mirroring the gating in settings_clients.js (IGNITE-573).
export type ClientImportOption = {
	systemName: string;
	modalType: string;
	// CRM integrations (Salesforce / HubSpot / Pipedrive) pass intl through to their sync modal
	passIntl: boolean;
};

type Props = {
	clients: ClientRowData[];
	companyId: string;
	clientPermissionsProfile: Profile | null;
	importOptions: ClientImportOption[];
};

export const ClientsPageComponent: React.FC<Props> = ({clients, companyId, clientPermissionsProfile, importOptions}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const [modalState, setModalState] = useState<ModalState>({type: 'none'});
	const [searchText, setSearchText] = useState('');

	const handleClientPermissions = () => {
		if (!clientPermissionsProfile) {
			showModal({type: MODAL_TYPE.MUTATION_ERROR});
			return;
		}
		showModal({
			type: MODAL_TYPE.CLIENT_PERMISSIONS,
			profile: clientPermissionsProfile,
			permissions: CLIENT_PERMISSIONS_WITH_GUEST_USER,
			profileNameCanBeEdited: false,
			profileCanBeEdited: true,
			headerText: formatMessage({id: 'settings.edit_client_permissions'}),
			hideProfileName: true,
			description: formatMessage({id: 'client_page.client_description'}),
		});
	};

	const handleAddClient = () => {
		if (hasFeatureFlag('new_clients_feature')) {
			setModalState({type: 'createClient'});
		} else {
			showModal({type: MODAL_TYPE.ADD_CLIENT});
		}
	};

	const handleImport = (option: ClientImportOption) => {
		showModal({
			type: option.modalType,
			companyId,
			...(option.passIntl ? {intl} : {}),
		});
	};

	const handleClientCreated = (clientId: string, clientName: string) => {
		createToast({
			duration: 5000,
			message: formatMessage({id: 'clients_page.create_client.success_snackbar'}, {name: clientName}),
		});
		setModalState({type: 'createContactConfirm', clientId, clientName});
	};

	// Duplicate detection deliberately reads the full list, not the filtered one, so an active
	// search cannot hide an existing name from the create-client check.
	const existingClientNames = useMemo(() => clients.map(c => c.name), [clients]);
	const redesignedHeader = hasFeatureFlag(FEATURE_FLAGS.CLIENT_LIST_SEARCH_AND_DESIGN);

	const trimmedSearch = searchText.trim();
	const term = redesignedHeader ? normalizeForSearch(trimmedSearch) : '';

	// Names are normalized once per list rather than once per keystroke: normalizing is the
	// expensive half of the match (NFD decomposition plus a Unicode-property regex), and the term
	// changes far more often than the list does. The search term lives in this component, so
	// typing does not re-render the Relay container above and `clients` keeps its identity.
	const normalizedNames = useMemo(() => clients.map(client => normalizeForSearch(client.name || '')), [clients]);

	// Filtering the loaded rows rather than refetching: the whole client list arrives in one query,
	// so matching is synchronous and there is no window where the term and the rows disagree.
	const visibleClients = useMemo(
		() => (term === '' ? clients : clients.filter((_, i) => normalizedNames[i].includes(term))),
		[clients, normalizedNames, term]
	);
	const noSearchMatches = term !== '' && visibleClients.length === 0;

	return (
		<PageWrapper data-cy="clients-page">
			{redesignedHeader ? (
				<ClientsPageHeader
					importOptions={importOptions}
					searchText={searchText}
					onSearchChange={setSearchText}
					onClientPermissions={handleClientPermissions}
					onImport={handleImport}
					onAddClient={handleAddClient}
				/>
			) : (
				<>
					<HeadingRow>
						<Heading>{formatMessage({id: 'clients_page.heading'})}</Heading>
						<ButtonGroup>
							{importOptions.map(option => (
								<SecondaryButton
									key={option.modalType}
									type="button"
									data-cy={`import-from-${option.systemName.toLowerCase()}-button`}
									onClick={() => handleImport(option)}
								>
									{formatMessage({id: 'settings_clients.import_from'}, {systemName: option.systemName})}
								</SecondaryButton>
							))}
							<SecondaryButton
								type="button"
								data-cy="edit-client-permissions-button"
								onClick={handleClientPermissions}
							>
								{formatMessage({id: 'clients_page.client_permissions'})}
							</SecondaryButton>
							<SecondaryButton type="button" data-cy="add-client-button" onClick={handleAddClient}>
								{formatMessage({id: 'clients_page.add_client'})}
							</SecondaryButton>
						</ButtonGroup>
					</HeadingRow>

					<Description>{formatMessage({id: 'clients_page.description'})}</Description>
				</>
			)}

			<ScrollContainer>
				<ClientsTable>
					<TableHead>
						<tr>
							<Th style={{width: '25%'}}>{formatMessage({id: 'clients_page.column.name'})}</Th>
							<Th style={{width: '100%'}}>{formatMessage({id: 'clients_page.column.notes'})}</Th>
							<ThRight>{formatMessage({id: 'clients_page.column.projects'})}</ThRight>
							{/* Integration icons — headerless column. table-layout: auto expands it to fit the row
							    with the most icons; sparser rows leave it blank. */}
							<Th />
							{/* Actions kebab — content-sized, stays pinned at the right edge for every row. */}
							<Th />
						</tr>
					</TableHead>
					<tbody>
						{noSearchMatches ? (
							<tr>
								<EmptyStateCell colSpan={5} data-cy="clients-search-no-match">
									<Text color="secondary">
										{formatMessage({id: 'search.no_match'}, {searchText: trimmedSearch})}
									</Text>
								</EmptyStateCell>
							</tr>
						) : (
							visibleClients.map(client => <ClientsListItem key={client.id} client={client} />)
						)}
					</tbody>
				</ClientsTable>
			</ScrollContainer>

			{modalState.type === 'createClient' && (
				<CreateClientModal
					companyId={companyId}
					existingClientNames={existingClientNames}
					onClose={() => setModalState({type: 'none'})}
					onClientCreated={handleClientCreated}
				/>
			)}

			{modalState.type === 'createContactConfirm' && (
				<CreateContactConfirmModal
					clientName={modalState.clientName}
					onAddContacts={() => {
						if (!modalState.clientId) {
							// Defensive: should never happen; CreateClientMutation always returns an id.
							setModalState({type: 'none'});
							return;
						}
						setModalState({
							type: 'createContact',
							clientId: modalState.clientId,
							clientName: modalState.clientName,
						});
					}}
					onSkip={() => setModalState({type: 'none'})}
				/>
			)}

			{modalState.type === 'createContact' && (
				<ClientContactModalContainer
					key={modalState.clientId}
					mode="contact"
					entryPoint="client-page"
					clientId={modalState.clientId}
					preSelectedClientName={modalState.clientName}
					onClose={() => setModalState({type: 'none'})}
				/>
			)}
		</PageWrapper>
	);
};

// ---------------------------------------------------------------------------
// Relay wiring — maps viewer.company.clients/profiles to component props
// ---------------------------------------------------------------------------

type ClientNode = {
	id: string;
	name: string;
	notes: string | null;
	projectCount: number | null;
	logoId: string | null;
	hubspotCompanyId: string | null;
	pipedriveOrganizationId: string | null;
	xeroContactId: string | null;
	economicCustomerId: string | null;
	quickbooksCustomerId: string | null;
	salesforceAccountId: string | null;
	sageIntacctCustomerId: string | null;
};

type ViewerProps = {
	viewer: {
		// Company-wide invoices, matched by client id to flag clients that cannot be deleted.
		// Mirrors the old clients page (settings_clients.js) which greys delete on any invoice (IGNITE-569).
		invoicesV2: {
			edges: Array<{node: {client: {id: string} | null} | null} | null> | null;
		} | null;
		// Company-wide contacts, matched by client id to flag clients that cannot be deleted (IGNITE-597).
		// Returns ALL statuses (active + inactive); the resolver returns an empty connection when the
		// new_client_contacts_feature flag is off, so this fetch is always safe.
		clientContacts: {
			edges: Array<{node: {client: {id: string} | null} | null} | null> | null;
		} | null;
		company: {
			id: string;
			hubspotAccountId: string | null;
			pipedriveCompanyDomain: string | null;
			economicEnabled: boolean | null;
			quickbooksEnabled: boolean | null;
			newSalesforceEnabled: boolean | null;
			sageIntacctConnectionEnabled: boolean | null;
			xeroEnabled: boolean | null;
			newXeroEnabled: boolean | null;
			clients: {
				edges: Array<{node: ClientNode}>;
			};
			profiles: {
				edges: Array<{node: Profile}>;
			};
		};
	};
};

// Decide which "Import from X" buttons to show. Mirrors settings_clients.js exactly:
// each integration is gated on its company-level connection flag AND the relevant
// finance/module/feature availability check (IGNITE-573).
function getImportOptions(company: ViewerProps['viewer']['company'] | undefined): ClientImportOption[] {
	if (!company) return [];
	const options: ClientImportOption[] = [];
	const hasFinance = CompanySetupUtil.hasFinance();
	if (company.economicEnabled && hasFinance) {
		options.push({systemName: 'E-conomic', modalType: MODAL_TYPE.ECONOMIC_SYNC_CLIENTS, passIntl: false});
	}
	if (company.newXeroEnabled && hasFinance) {
		options.push({systemName: 'Xero', modalType: MODAL_TYPE.XERO_SYNC_CLIENTS, passIntl: false});
	}
	if (company.quickbooksEnabled && hasFinance) {
		options.push({systemName: 'QuickBooks', modalType: MODAL_TYPE.QUICKBOOKS_SYNC_CLIENTS, passIntl: false});
	}
	if (company.newSalesforceEnabled && hasModule(MODULE_TYPES.SALESFORCE)) {
		options.push({systemName: 'Salesforce', modalType: MODAL_TYPE.SALESFORCE_SYNC_CLIENTS, passIntl: true});
	}
	if (company.hubspotAccountId && Util.hasHubspot()) {
		options.push({systemName: 'HubSpot', modalType: MODAL_TYPE.HUBSPOT_SYNC_CLIENTS, passIntl: true});
	}
	if (company.pipedriveCompanyDomain && hasModule(MODULE_TYPES.PIPEDRIVE)) {
		options.push({systemName: 'Pipedrive', modalType: MODAL_TYPE.PIPEDRIVE_SYNC_CLIENTS, passIntl: true});
	}
	return options;
}

const ClientsPageRelay: React.FC<ViewerProps> = ({viewer}) => {
	const companyId = viewer.company?.id ?? '';
	const hubspotAccountId = viewer.company?.hubspotAccountId ?? null;
	const pipedriveCompanyDomain = viewer.company?.pipedriveCompanyDomain ?? null;
	const salesforceEnabled = !!viewer.company?.newSalesforceEnabled;

	// The Salesforce "Open in" link needs the org's instance URL. The stored company external-ref
	// (company.salesforceUrl) is not reliably populated, so — like the old clients page
	// (settings_clients.js) — fetch it live from the integration endpoint when Salesforce is enabled.
	const [salesforceUrl, setSalesforceUrl] = useState<string | null>(null);
	useEffect(() => {
		let active = true;
		if (salesforceEnabled) {
			getSalesforceInstanceUrl()
				.then(url => {
					if (active) setSalesforceUrl(url ?? null);
				})
				.catch(() => {
					if (active) setSalesforceUrl(null);
				});
		}
		return () => {
			active = false;
		};
	}, [salesforceEnabled]);

	// A client cannot be deleted once it has any invoice — same rule the old clients page uses.
	const invoicedClientIds = new Set<string>(
		(viewer.invoicesV2?.edges ?? []).map(e => e?.node?.client?.id).filter((id): id is string => id != null)
	);

	// A client cannot be deleted once it owns any contact (active or inactive) — IGNITE-597.
	// Gated on the base client-contacts feature (flag + module), the same pair that controls whether a
	// client can have contacts at all. NOT gated on extending_client_contacts (that governs project
	// assignment, not ownership).
	const contactsFeatureOn =
		hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENT_CONTACTS_FEATURE) && hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE);
	const clientIdsWithContacts = buildClientIdsWithContacts(viewer.clientContacts);

	// Each integration icon mirrors the old clients page (settings_clients.js): an id is only surfaced
	// when its company-level integration is enabled, so a stale id on a disconnected company stays hidden.
	// QuickBooks is intentionally ungated, matching the old page.
	const hubspotEnabled = !!hubspotAccountId;
	const xeroEnabled = !!(viewer.company?.xeroEnabled || viewer.company?.newXeroEnabled);
	const economicEnabled = !!viewer.company?.economicEnabled;
	const sageIntacctEnabled = !!viewer.company?.sageIntacctConnectionEnabled;
	const pipedriveEnabled = !!pipedriveCompanyDomain;

	const clients: ClientRowData[] = (viewer.company?.clients?.edges ?? [])
		.filter((edge): edge is {node: ClientNode} => edge?.node != null)
		.map(({node}) => ({
			id: node.id,
			name: node.name,
			notes: node.notes ?? null,
			projectCount: node.projectCount ?? 0,
			logoId: node.logoId ?? null,
			companyId,
			hubspotCompanyId: hubspotEnabled ? node.hubspotCompanyId ?? null : null,
			xeroContactId: xeroEnabled ? node.xeroContactId ?? null : null,
			economicCustomerId: economicEnabled ? node.economicCustomerId ?? null : null,
			quickbooksCustomerId: node.quickbooksCustomerId ?? null,
			salesforceAccountId: salesforceEnabled ? node.salesforceAccountId ?? null : null,
			sageIntacctCustomerId: sageIntacctEnabled ? node.sageIntacctCustomerId ?? null : null,
			pipedriveOrganizationId: pipedriveEnabled ? node.pipedriveOrganizationId ?? null : null,
			hasInvoices: invoicedClientIds.has(node.id),
			hasContacts: computeClientHasContacts(node.id, clientIdsWithContacts, contactsFeatureOn),
			hubspotAccountId,
			pipedriveCompanyDomain,
			salesforceUrl,
		}));
	const clientPermissionsProfile =
		(viewer.company?.profiles?.edges ?? [])
			.map(e => e.node)
			.find(p => p.permissions.includes(PERMISSION_TYPE.IS_CLIENT_USER)) ?? null;
	return (
		<ClientsPageComponent
			clients={clients}
			companyId={companyId}
			clientPermissionsProfile={clientPermissionsProfile}
			importOptions={getImportOptions(viewer.company)}
		/>
	);
};

export const ClientsPageQuery = graphql`
	query ClientsPage_Query {
		viewer {
			actualPersonId
			...ClientsPage_viewer
		}
	}
`;

// `clients(first: 10000)` matches every other consumer of this connection — the legacy clients
// page and the client pickers all ask for 10000. The resolver has no real pagination: it fetches
// the company's clients in full and slices the array, so asking for all of them costs the backend
// nothing extra, and it lets search match every client rather than only the rows scrolled into view.
export default createFragmentContainer(ClientsPageRelay, {
	viewer: graphql`
		fragment ClientsPage_viewer on Viewer {
			id
			invoicesV2(first: 100000) {
				edges {
					node {
						id
						client {
							id
						}
					}
				}
			}
			clientContacts(first: 100000) {
				edges {
					node {
						client {
							id
						}
					}
				}
			}
			company {
				id
				hubspotAccountId
				pipedriveCompanyDomain
				economicEnabled
				quickbooksEnabled
				newSalesforceEnabled
				sageIntacctConnectionEnabled
				xeroEnabled
				newXeroEnabled
				clients(first: 10000) @connection(key: "Company_clients", filters: []) {
					edges {
						node {
							id
							name
							notes
							logoId
							projectCount
							hubspotCompanyId
							pipedriveOrganizationId
							xeroContactId
							economicCustomerId
							quickbooksCustomerId
							salesforceAccountId
							sageIntacctCustomerId
						}
					}
				}
				profiles(first: 10000) {
					edges {
						node {
							id
							name
							permissions
						}
					}
				}
			}
		}
	`,
});
